import { TextButtonLink } from '@creditornot/cb-button';
import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import { pxToRem } from '@creditornot/cb-utils';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { typographyCss } from 'common/typography';
import CampaignFormItem from 'modules/campaigns/components/CampaignFormItem';

type FreeTrialFormContentEnabledProps = {
  freeTrialToggleChangeHandler: (enabled: boolean) => void;
  adsFreeTrialConfig: {
    formattedWeeklyBudgetWithCurrency: string;
    durationWeeks: number;
  };
};

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(16)};
`;

const Title = styled.div`
  ${typographyCss.Title1};
  font-weight: 700;
`;

const Header = styled.div<{ enabled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ enabled }) => (enabled ? colors.wolt8 : 'transparent')};
  padding: ${pxToRem(8)} ${pxToRem(16)};
`;

const DefinitionList = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
`;

const LineItemPrimary = styled.dt`
  ${typographyCss.Body3};
  color: ${cbSemanticCssVars.textSecondary};
`;

const LineItemSecondary = styled.dd`
  ${typographyCss.Body3};
  color: ${cbSemanticCssVars.textPrimary};
`;

export const FreeTrialFormContentEnabled = ({
  freeTrialToggleChangeHandler,
  adsFreeTrialConfig: { formattedWeeklyBudgetWithCurrency, durationWeeks },
}: FreeTrialFormContentEnabledProps) => {
  const trialLengthDays = durationWeeks * 7;
  return (
    <CampaignFormItem
      data-test-id="free-trial-form-content-enabled"
      banner={
        <Header enabled>
          <TitleContainer>
            <img src={`${process.env.CDN_URL}/ads/free-trial-coin.png`} alt="" />
            <Title>
              <FormattedMessage
                id="campaigns.ads.free-trials.create-campaign-header-trial-applied"
                values={{ trialLength: durationWeeks }}
              />
            </Title>
          </TitleContainer>
          <TextButtonLink
            data-test-id="free-trial-disable-cta"
            onClick={() => {
              freeTrialToggleChangeHandler(false);
            }}
          >
            <FormattedMessage id="campaigns.ads.free-trials.create-campaign-turn-off-cta" />
          </TextButtonLink>
        </Header>
      }
      value={
        <DefinitionList>
          <LineItemPrimary>
            <FormattedMessage id="campaigns.ads.free-trials.create-campaign-description-budget" />
          </LineItemPrimary>
          <LineItemSecondary>
            <FormattedMessage
              id="campaigns.ads.free-trials.create-campaign-description-budget-per-week-free"
              values={{
                budget: formattedWeeklyBudgetWithCurrency,
                tag: innerText => <Tag variant="lightBlue">{innerText}</Tag>,
              }}
            />
          </LineItemSecondary>
          <LineItemPrimary>
            <FormattedMessage id="campaigns.ads.free-trials-duration-key" />
          </LineItemPrimary>
          <LineItemSecondary>
            <FormattedMessage
              id="campaigns.ads.free-trials.create-campaign-description-duration-first-days"
              values={{ numberOfDays: trialLengthDays }}
            />
          </LineItemSecondary>
        </DefinitionList>
      }
      title={<FormattedMessage id="campaigns.ads.free-trials.create-campaign-description-title" />}
      subtitle={
        <FormattedMessage
          id="campaigns.ads.free-trials.create-campaign-description-body-new"
          values={{
            weekCount: durationWeeks,
            amount: formattedWeeklyBudgetWithCurrency,
            bold: innerText => <strong>{innerText}</strong>,
          }}
        />
      }
    ></CampaignFormItem>
  );
};
